import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageCircle } from "lucide-react";

const SALES_PHONE = "+917000528933";
const WHATSAPP_NUMBER = "917000528933";
const WHATSAPP_MSG = encodeURIComponent("Hi IronByte, I'd like to talk to your sales team.");
const LONG_PRESS_MS = 450;

export function CallSalesButton() {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const longPressed = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, { passive: true });
    const id = setTimeout(close, 4000);
    return () => {
      window.removeEventListener("scroll", close);
      clearTimeout(id);
    };
  }, [open]);

  const startPress = () => {
    longPressed.current = false;
    timer.current = setTimeout(() => {
      longPressed.current = true;
      window.location.href = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MSG}`;
    }, LONG_PRESS_MS);
  };
  const endPress = () => {
    if (timer.current) clearTimeout(timer.current);
  };
  const onClick = (e) => {
    if (longPressed.current) {
      e.preventDefault();
      return;
    }
    // one-tap call
    window.location.href = `tel:${SALES_PHONE}`;
  };

  return (
    <div className="fixed z-40 left-4 sm:left-6 bottom-[calc(4.25rem+env(safe-area-inset-bottom))] sm:bottom-8 flex flex-col items-start gap-2">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ duration: 0.18 }}
            className="flex flex-col gap-2"
          >
            <a
              href={`tel:${SALES_PHONE}`}
              data-testid="call-sales-call-option"
              onClick={() => setOpen(false)}
              className="hero-glass h-10 pl-2.5 pr-4 rounded-full border border-ib-blue/40 flex items-center gap-2 text-ib-text active:scale-95 transition-transform shadow-lg shadow-black/20"
            >
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-ib-blue">
                <Phone className="w-3 h-3 text-white" />
              </span>
              <span className="font-outfit font-semibold text-xs leading-none">Call sales</span>
            </a>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MSG}`}
              data-testid="call-sales-whatsapp-option"
              onClick={() => setOpen(false)}
              className="hero-glass h-10 pl-2.5 pr-4 rounded-full border border-emerald-400/40 flex items-center gap-2 text-ib-text active:scale-95 transition-transform shadow-lg shadow-black/20"
            >
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500">
                <MessageCircle className="w-3 h-3 text-white" />
              </span>
              <span className="font-outfit font-semibold text-xs leading-none">WhatsApp sales</span>
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      <a
        href={`tel:${SALES_PHONE}`}
        data-testid="call-sales-fab"
        aria-label="Call IronByte sales. Long-press for WhatsApp."
        onClick={onClick}
        onContextMenu={(e) => e.preventDefault()}
        onTouchStart={startPress}
        onTouchEnd={endPress}
        onTouchMove={endPress}
        onMouseDown={startPress}
        onMouseUp={endPress}
        className="hero-glass h-11 pl-3 pr-2 rounded-full border border-ib-blue/40 flex items-center gap-2 text-ib-text active:scale-95 transition-transform shadow-lg shadow-ib-blue/20 select-none"
      >
        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-ib-blue">
          <Phone className="w-3.5 h-3.5 text-white" />
        </span>
        <span className="font-outfit font-semibold text-sm leading-none">Call sales</span>
        <button
          type="button"
          aria-label="More contact options"
          data-testid="call-sales-more-btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          className="flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500 ml-0.5"
        >
          <MessageCircle className="w-3.5 h-3.5 text-white" />
        </button>
      </a>
    </div>
  );
}
